import { getEndpoints } from "../../Lib/Config/EndPoints/EndPoints";
import { apiRequest } from "../../Lib/Utils/ApiRequest";
import { IApiResponse, IBookingResponse } from "../../Orchestrator/Types/Index";

const inflight = new Map<string, Promise<IApiResponse<IBookingResponse>>>();

/**
 * Drop a booking's in-flight read so the NEXT getBooking() for it issues a
 * fresh request instead of joining one that is already on the wire.
 *
 * `inflight` is keyed by booking id alone, with no notion of when a request
 * left. A read issued AFTER a mutation therefore joins a request that left
 * BEFORE it and resolves with pre-mutation data. Captured on upgrade-to-
 * premium (booking 60627920, prelive):
 *
 *   27.162  GET /bookings/{id}          <- booking-details refresh, in flight
 *   27.427  POST /exchange-operations   <- the upgrade's pre-exchange commits
 *   ~27.43  getBooking(id)              <- upgrade's read JOINS the 27.162 one
 *   27.567  ...resolves with the PRE-exchange booking: no PREBOOKED premium
 *           offer, exchangeOperations: []
 *
 * The upgrade summary is then built from a booking that has no upgrade in it,
 * so /checkout renders the ticket the user was exchanging AWAY from, at full
 * fare. Every mutating booking call must evict the id here so no later reader
 * can be served a snapshot from before the mutation.
 *
 * This does not abort the in-flight request — callers that asked before the
 * mutation still get the answer they asked for, which is correct.
 *
 * DEPENDENCY: this evicts the in-flight map only. Booking GETs bypass
 * cacheManager today solely because ApiConfigManager.getApiConfig matches
 * config paths by EXACT string equality, so `/bookings/{id}` can never match a
 * cache rule. If that matching ever becomes pattern-based, this function must
 * also evict cacheManager for the booking URL or requirement 1 silently stops
 * holding, with no failing test.
 *
 * SCOPE, so the next investigator does not read this as "settled": it fixes
 * what a post-mutation read RESOLVES WITH. It does not order the WRITE-BACKS.
 * The pre-mutation reader above (GetBookingDetailDataAction) awaits several
 * more actions after its response lands and only then writes
 * bookingResponseAtom, so it can still clobber the fresh value written by the
 * upgrade. Same two actors, same visible symptom, different mechanism —
 * untouched here.
 */
export function invalidateBookingInflight(bookingId: string): void {
    inflight.delete(bookingId);
}

export async function getBooking(bookingId: string, showLoader?: boolean): Promise<IApiResponse<IBookingResponse>> {
    const existing = inflight.get(bookingId);
    if (existing) return existing;

    const promise = (async () => {
        const ENDPOINTS = await getEndpoints();
        const endpoint = `${ENDPOINTS.getBooking}/${bookingId}`;
        return apiRequest<IBookingResponse>(endpoint, 'GET', {}, 'none', undefined, undefined, showLoader);
    })().finally(() => {
        // Safe despite `promise` being in TDZ where this closure is created:
        // an async IIFE never settles synchronously, so the callback cannot
        // run before the const binding initialises.
        // Only clear our OWN entry: after an invalidateBookingInflight() a
        // newer request may already own this key, and deleting it would put
        // that one back on the "no one is fetching" path for later callers.
        if (inflight.get(bookingId) === promise) inflight.delete(bookingId);
    });

    inflight.set(bookingId, promise);
    return promise;
}
